import { useRef, useState } from 'react';
import { Link } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import { api, type DashboardDto, type HoldingsResponse, type Money } from '../lib/api.js';
import { MoneyText, formatCompact, formatExact } from '../components/MoneyText.js';
import { CountUp } from '../components/CountUp.js';
import { DayGain } from '../components/DayGain.js';

/**
 * The Glance (docs/SCREEN-SPECS.md §1): nouns float, verbs sink; one scroll
 * ranked position → change → allocation → goals → movers → provenance.
 * No charts yet — allocation renders as labelled bars until the chart layer
 * lands with its dataviz pass.
 */
export function Home() {
  const { data, isPending, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardDto>('/dashboard'),
  });

  /*
   * Today's move comes from the holdings read model, which already computes it
   * per class and for the whole portfolio. Sharing the ⌘K palette's cache key
   * means the list is fetched once and serves the palette, this strip, and a
   * subsequent visit to Wealth — no third endpoint, no duplicated arithmetic
   * that could drift from what the Wealth screen reports.
   */
  const holdings = useQuery({
    queryKey: ['holdings', null, false, 'type', 'name'],
    queryFn: () => api.get<HoldingsResponse>('/holdings'),
    staleTime: 60_000,
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const { netWorth, allocation, movers, campaigns, freshness } = data;

  return (
    <div>
      <h1 className="page-title">Home</h1>

      {/*
        Above the hero, deliberately.

        A member whose statements have stopped changes what the number below is
        WORTH, so it cannot sit beneath it — this was five months of one
        person's portfolio going unrecorded while the only trace was grey text
        at the foot of the page. It links to Mailboxes because that is where
        the cause almost always is: a rule that stopped matching, or a password
        that stopped working.
      */}
      {data.alert && (
        <div className="alertbar" role="status">
          <span className="alertbar-glyph" aria-hidden>
            ⚠
          </span>
          <span className="alertbar-body">
            <span className="alertbar-text">{data.alert.message}</span>
            <Link to="/mailboxes" className="linkish alertbar-link">
              Check mailboxes →
            </Link>
          </span>
        </div>
      )}

      {/* Hero — display only; drill-downs live lower, in reach. */}
      <section className="hero-panel">
        <span className="eyebrow">Net worth · as of {netWorth.total.asOf.slice(0, 10)}</span>
        <div className="metric-line">
          <span className="hero-money">
            <CountUp paise={netWorth.total.money.amount} />
          </span>
          {netWorth.delta && netWorth.delta.percent !== null && (
            <span
              className={`badge ${netWorth.delta.money.amount >= 0 ? 'badge-good' : 'badge-bad'}`}
              style={{ font: 'var(--type-footnote)' }}
            >
              {netWorth.delta.percent > 0 ? '▲' : '▼'} {Math.abs(netWorth.delta.percent).toFixed(2)}% ·{' '}
              {formatCompact(Math.abs(netWorth.delta.money.amount))}
            </span>
          )}
        </div>
        {data.trend.length > 1 && <Sparkline points={data.trend} />}
      </section>

      {/* Confirmed vs projected — the split every displayed value promises. */}
      <div className="stat-grid">
        <div className="stat">
          <span className="stat-label">Confirmed</span>
          <span className="stat-value">{formatCompact(netWorth.confirmed.amount)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Projected</span>
          <span className="stat-value">{formatCompact(netWorth.projected.amount)}</span>
        </div>
      </div>

      <Card title="Allocation">
        {/*
          A first-run database has no holdings, and mapping an empty array
          rendered a blank card — a box with a title and nothing in it, which
          reads as a loading failure rather than "there is nothing here yet".
          The empty state says which action produces content, so the screen
          teaches the next step instead of just being empty.
        */}
        {allocation.slices.length === 0 && <FirstRun />}
        {allocation.slices.length > 0 && <AllocationDonut slices={allocation.slices} />}
      </Card>

      {campaigns.length > 0 && (
        <Card title="Goals">
          {campaigns.map((c) => (
            <Row
              key={c.id}
              left={
                <span className="row-title" style={{ font: 'var(--type-body)' }}>
                  <span
                    aria-hidden="true"
                    className="dot"
                    style={{
                      background: c.hueSlot ? `var(--color-slot-${c.hueSlot})` : 'var(--color-label-tertiary)',
                    }}
                  />
                  {c.name}
                  <span className="row-meta" style={{ display: 'inline' }}>
                    {' '}
                    · {c.holdingCount} holding{c.holdingCount === 1 ? '' : 's'}
                  </span>
                </span>
              }
              right={
                c.corpusPaise !== null ? (
                  <MoneyText variant="compact" value={{ amount: c.corpusPaise, currency: 'INR' }} />
                ) : (
                  '—'
                )
              }
            />
          ))}
        </Card>
      )}

      {/*
        Today, immediately before Movers — and deliberately in that order. This
        strip says HOW MUCH the family moved; Movers says WHICH holdings did the
        moving. Magnitude then attribution: the second card is the answer to the
        question the first one raises, so they are read as one thought.

        Rows appear only for classes that can actually be priced daily. EPF has
        no daily NAV, so it is absent rather than a row of dashes — an asset
        that cannot move today has nothing to report today.
      */}
      {holdings.data && (
        <TodayCard
          overall={holdings.data.dayGain}
          sections={holdings.data.sections
            .filter((s) => s.class !== 'epf')
            .map((s) => ({ class: s.class, dayGain: s.dayGain }))}
        />
      )}

      {movers.length > 0 && (
        <Card title="Movers">
          {movers.map((m) => (
            <Row
              key={m.holdingId}
              left={
                <Link to={`/wealth/holding/${m.holdingId}`}>
                  <span className="row-title" style={{ font: 'var(--type-body)' }}>
                    {m.name}
                  </span>
                  <span className="row-meta">{m.member}</span>
                </Link>
              }
              right={
                <span className={m.percent >= 0 ? 'delta-pos' : 'delta-neg'}>
                  {m.percent >= 0 ? '▲' : '▼'} {Math.abs(m.percent).toFixed(1)}%
                </span>
              }
            />
          ))}
        </Card>
      )}

      {/*
        Provenance, per person — two facts that were being confused for one.

        "Confirmed to" is how current the EVIDENCE is: the newest statement
        covering that member's folios. "Last investment" is when money last
        went in. A portfolio can be entirely up to date and correct while
        nothing new has been bought for months, and reading the second as if it
        were the first is what made the dashboard accuse a member of going
        silent when his folios had been confirmed days earlier.

        So the investment date is shown plainly and never coloured: it is
        information a family may well want, not a fault.
      */}
      <Card title="Whose figures rest on what">
        {freshness.members.map((m) => (
          <Row
            key={m.memberId}
            left={
              <span>
                <span className="row-title" style={{ font: 'var(--type-body)' }}>
                  {m.name}
                </span>
                <span className="row-meta">
                  {m.openFolios} folio{m.openFolios === 1 ? '' : 's'} · last investment{' '}
                  {m.lastInvestmentAt ?? 'none recorded'}
                </span>
              </span>
            }
            right={
              <span style={{ textAlign: 'right' }}>
                <span className="row-detail state-muted">confirmed to</span>
                <span className="money-cell" style={{ font: 'var(--type-subhead)' }}>
                  {m.confirmedTo ?? '—'}
                </span>
              </span>
            }
          />
        ))}
      </Card>
    </div>
  );
}

/**
 * Today's move, split the way the family holds it.
 *
 * The whole-portfolio figure sits in the card's title because it is the
 * headline; the per-class rows below decompose it. A class whose move is
 * unknown still gets its row — the dash is informative here, since the reader
 * is being told the split and needs to know a piece of it is missing.
 */
function TodayCard({
  overall,
  sections,
}: {
  overall: Money | null;
  sections: { class: string; dayGain: Money | null }[];
}) {
  if (sections.length === 0) return null;
  const label = (k: string) => (k === 'mf' ? 'Mutual funds' : k === 'equity' ? 'Stocks' : k.toUpperCase());
  return (
    <Card
      title={
        <>
          <span>Today</span>
          <DayGain money={overall} size="chip" />
        </>
      }
    >
      {sections.map((s) => (
        <Row
          key={s.class}
          left={
            <span className="row-title" style={{ font: 'var(--type-body)' }}>
              {label(s.class)}
            </span>
          }
          right={<DayGain money={s.dayGain} />}
        />
      ))}
    </Card>
  );
}

/** The year's shape, whisper-quiet behind the number. No axes — the hero
 * text above IS the label, and Wealth carries the full chart. */
function Sparkline({ points }: { points: { asOf: string; valuePaise: number }[] }) {
  const W = 600;
  const H = 46;
  const wrap = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const min = Math.min(...points.map((p) => p.valuePaise));
  const max = Math.max(...points.map((p) => p.valuePaise));
  const span = Math.max(1, max - min);
  const xOf = (i: number) => (i / (points.length - 1)) * W;
  const yOf = (v: number) => H - 4 - ((v - min) / span) * (H - 8);
  const pt = (p: { valuePaise: number }, i: number) => `${xOf(i).toFixed(1)},${yOf(p.valuePaise).toFixed(1)}`;
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${pt(p, i)}`).join(' ');

  /*
   * Snap to the nearest month rather than tracking the pointer's exact x: the
   * reader is aiming at an observation, not at a 2px stroke, and every value
   * between two months is interpolation this chart must not appear to assert.
   */
  function onMove(e: React.PointerEvent) {
    const box = wrap.current?.getBoundingClientRect();
    if (!box) return;
    const x = ((e.clientX - box.left) / box.width) * W;
    let best = 0;
    for (let i = 1; i < points.length; i += 1) {
      if (Math.abs(xOf(i) - x) < Math.abs(xOf(best) - x)) best = i;
    }
    setHover(best);
  }

  const h = hover === null ? null : points[hover]!;
  const hx = hover === null ? 0 : xOf(hover);

  return (
    <div
      className="hero-spark-wrap"
      ref={wrap}
      onPointerMove={onMove}
      onPointerLeave={() => setHover(null)}
    >
      <svg className="hero-spark" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Net worth, last 12 months">
        <path d={`${line} L${W},${H} L0,${H} Z`} className="hero-spark-fill" />
        <path d={line} className="hero-spark-line" />
        {h && (
          <g>
            <line x1={hx} x2={hx} y1={0} y2={H} className="chart-crosshair" />
            <circle cx={hx} cy={yOf(h.valuePaise)} r={4} className="hero-spark-dot" />
          </g>
        )}
      </svg>
      {h && (
        <div
          className="chart-tip"
          style={{
            left: `${(hx / W) * 100}%`,
            transform: `translateX(${hx > W * 0.7 ? '-100%' : hx < W * 0.3 ? '0' : '-50%'})`,
          }}
        >
          <strong>{formatExact(h.valuePaise)}</strong>
          <span>{h.asOf}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Part-to-whole at a glance: three slices, slot colors in fixed order, the
 * 2px surface gap doing the separating (mark spec), total in the center.
 * The legend rows carry every value — nothing is gated behind hover.
 */
function AllocationDonut({
  slices,
}: {
  slices: { class: string; value: { amount: number; currency: 'INR' }; pct: number }[];
}) {
  const R = 52;
  const STROKE = 16;
  const C = 2 * Math.PI * R;
  const total = slices.reduce((a, s) => a + s.value.amount, 0);
  const label = (k: string) => (k === 'mf' ? 'Mutual funds' : k === 'equity' ? 'Stocks' : k.toUpperCase());
  const slot = (k: string, i: number) => (k === 'mf' ? 1 : k === 'equity' ? 2 : ((i % 8) + 3));
  let offset = 0;
  const GAP = 2.5; // the surface gap, in circumference units
  return (
    <div className="donut-wrap">
      <svg width="140" height="140" viewBox="0 0 140 140" role="img" aria-label="Allocation by asset class">
        {slices.map((s, i) => {
          const frac = total > 0 ? s.value.amount / total : 0;
          const len = Math.max(0, frac * C - GAP);
          const el = (
            <circle
              key={s.class}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={`var(--color-slot-${slot(s.class, i)})`}
              strokeWidth={STROKE}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
            >
              <title>{`${label(s.class)} · ${formatCompact(s.value.amount)} · ${s.pct}%`}</title>
            </circle>
          );
          offset += frac * C;
          return el;
        })}
        <text x="70" y="66" textAnchor="middle" style={{ font: 'var(--type-caption)', fill: 'var(--color-label-tertiary)' }}>
          Total
        </text>
        <text x="70" y="84" textAnchor="middle" style={{ font: 'var(--type-money-cell)', fill: 'var(--color-label-primary)' }}>
          {formatCompact(total)}
        </text>
      </svg>
      <div className="donut-legend">
        {slices.map((s, i) => (
          <div key={s.class} className="row">
            <div className="row-main" style={{ minHeight: 36 }}>
              <span className="row-left" style={{ font: 'var(--type-subhead)' }}>
                <span className="dot" style={{ background: `var(--color-slot-${slot(s.class, i)})` }} aria-hidden />
                {label(s.class)}
              </span>
              <span className="row-right state-muted" style={{ font: 'var(--type-subhead)', fontVariantNumeric: 'tabular-nums' }}>
                {formatCompact(s.value.amount)} · {s.pct}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * First run — the product's opening move.
 *
 * An empty dashboard that merely says "nothing here" makes the user do the
 * product design. This says the path out loud: connect a mailbox once and the
 * statements do the rest, or hand us one file right now. Both verbs link to
 * the screen that performs them.
 */
function FirstRun() {
  const steps: { n: string; title: string; body: string; to: string; cta: string }[] = [
    {
      n: '1',
      title: 'Connect a mailbox',
      body: 'CAS statements from CAMS and CDSL already arrive in your inbox. Connect it once — every statement is found, parsed and filed automatically.',
      to: '/mailboxes',
      cta: 'Connect →',
    },
    {
      n: '2',
      title: 'Or upload one statement',
      body: 'Have a CAS PDF on hand? Upload it and watch the family’s holdings appear in seconds.',
      to: '/wealth',
      cta: 'Go to Wealth →',
    },
    {
      n: '3',
      title: 'Then everything is automatic',
      body: 'Prices refresh daily, SIPs are detected from the rhythm of your purchases, and every change is logged.',
      to: '/insights',
      cta: 'See the log →',
    },
  ];
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {steps.map((s) => (
        <div key={s.n} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: 'var(--type-caption)',
              fontWeight: 700,
              color: 'var(--color-tint)',
              background: 'color-mix(in srgb, var(--color-tint) 12%, transparent)',
            }}
          >
            {s.n}
          </span>
          <span>
            <span style={{ display: 'block', font: 'var(--type-headline)' }}>{s.title}</span>
            <span className="page-sub" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
              {s.body}
            </span>
            <Link to={s.to} className="linkish" style={{ color: 'var(--color-tint)' }}>
              {s.cta}
            </Link>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Shared primitives — every list screen composes these. They delegate to the
 * component layer in styles/app.css; no visual decisions are made here, which
 * is what lets a restyle of app.css restyle every screen at once.
 * ------------------------------------------------------------------------- */

export function Card({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="card">
      <h2 className="card-title">{title}</h2>
      {children}
    </section>
  );
}

export function Row({
  left,
  right,
  footer,
  accent = false,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
  /**
   * An optional third slot on its own line beneath the row. It exists because
   * `left` is usually a link covering the whole label, and an interactive
   * control cannot live inside an anchor — clicking it would navigate rather
   * than act. Anything actionable that belongs to the row rather than to its
   * link goes here.
   */
  footer?: React.ReactNode;
  /** Draw the attention bar — the row has current-month activity. */
  accent?: boolean;
}) {
  return (
    <div className={`row${accent ? ' row-accent' : ''}`}>
      <div className="row-main">
        <span className="row-left">{left}</span>
        <span className="row-right">{right}</span>
      </div>
      {footer && <div className="row-footer">{footer}</div>}
    </div>
  );
}

export function Skeleton() {
  return (
    <div aria-busy="true">
      {[120, 46, 220, 220].map((h, i) => (
        <div key={i} className="skeleton-block" style={{ height: h }} />
      ))}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div role="alert" className="error-note">
      {message}
    </div>
  );
}
